/**
 * SEO helpers for the public Help-Center (`/help/{org-slug}/…`): absolute URL
 * construction, meta-description snippets derived from article bodies, and
 * safe JSON-LD serialization. Kept framework-agnostic so both `generateMetadata`
 * and the rendered pages can share one source of truth.
 */

import { orgPublicOriginFor } from "@v1/backend/logic/organizations/staff_host";

/** Public-facing origin serving `/help`, without a trailing slash. */
export function publicSiteOrigin(): string {
  return (
    process.env.NEXT_PUBLIC_SITE_URL ?? "https://app.hausverwalter.ai"
  ).replace(/\/$/, "");
}

/** Absolute canonical URL of a help-center landing page or article; host = org subdomain. */
export function helpCenterUrl(orgSlug: string, pageId?: string): string {
  const origin = orgPublicOriginFor(publicSiteOrigin(), orgSlug);
  const base = `${origin}/help/${encodeURIComponent(orgSlug)}`;
  return pageId ? `${base}/${pageId}` : base;
}

/** Absolute canonical URL of a knowledge space overview. */
export function helpSpaceUrl(orgSlug: string, spaceId: string): string {
  return `${helpCenterUrl(orgSlug)}/spaces/${encodeURIComponent(spaceId)}`;
}

/**
 * Flattens markdown into a plain-text meta-description snippet (~155 chars,
 * cut on a word boundary). Used as a fallback when an article has no authored
 * description, so Google gets a curated snippet instead of inventing one.
 */
export function deriveSnippet(markdown: string, maxLen = 155): string {
  const text = markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/^>\s?/gm, "")
    .replace(/^[-*+]\s+/gm, "")
    .replace(/[*_~]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= maxLen) return text;
  const truncated = text.slice(0, maxLen);
  const lastSpace = truncated.lastIndexOf(" ");
  const body = lastSpace > 40 ? truncated.slice(0, lastSpace) : truncated;
  return `${body.trimEnd()}…`;
}

/**
 * Serializes a JSON-LD object for inline `<script type="application/ld+json">`,
 * escaping `<` so a stray `</script>` in the data cannot break out of the tag.
 */
export function jsonLdHtml(data: unknown): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}
